import React, { useRef } from 'react';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import type { Options } from '@splidejs/splide';

import { ProductTile } from '@src/stories/ecommerce/Product/ProductTile';
import CarouselArrows from '@src/stories/elements/CarouselArrows/CarouselArrows';

import { getGlobalBreakpoints } from '@src/hooks/useMediaQuery';
import { useResponsiveBreakpoints } from '@src/hooks/useMediaQuery';
import { url } from 'inspector';

interface Product {
  id?: string | number;
  title: string;
  price: string;
  vendor?: string;
  image?: string;
  colors?: string;
  badge?: string;
}

interface HeaderSearchResultsProps {
  title?: string;
  products?: Product[];
  totalResults?: number;
  query?: string;
  className?: string;
  onSeeAllProducts?: () => void;
}

export const HeaderSearchResults: React.FC<HeaderSearchResultsProps> = ({
  title = 'Products (12)',
  products = [],
  settings,
  totalResults = 12,
  query = '',
  className = '',
  onSeeAllProducts
}) => {
  const {
    layout_type = 'carousel',
    layout_type_mobile = 'carousel',
    layout_type_tablet = 'carousel',
    layout_type_ultrawide = 'carousel',
    show_carousel_arrows = true,
    columns_vertical_scroll = 4,
    columns_vertical_scroll_mobile = 2,
    columns_vertical_scroll_tablet = 3,
    columns_vertical_scroll_ultrawide = 5,
    products_per_row = 4
  } = settings?.settings?.block_settings || {};

  const { isMobile, isTablet, isUltrawide } = useResponsiveBreakpoints();
  const globalBreakpoints = getGlobalBreakpoints();
  const { mobile: mobile_max, tablet: tablet_max, desktop: desktop_max } = globalBreakpoints;

  const splideRef = useRef<any>(null);

  const displayProducts = products.length > 0 ? products : [];
  const displayTitle = products.length > 0 ? `Products (${totalResults})` : title;

  const handlePrevious = () => {
    if (splideRef.current?.splide) {
      splideRef.current.splide.go('<');
    }
  };

  const handleNext = () => {
    if (splideRef.current?.splide) {
      splideRef.current.splide.go('>');
    }
  };

  const splideOptions: Options = {
    type: 'slide',
    perPage: products_per_row,
    padding: {
      right: 100
    },
    perMove: 1,
    gap: 16,
    arrows: false,
    pagination: false,
    breakpoints: {
      [tablet_max]: {
        perPage: Math.min(3, products_per_row),
        padding: {
          right: 100
        }
      },
      [mobile_max]: {
        perPage: Math.min(2, products_per_row),
        gap: 8,
        padding: {
          right: 0
        }
      }
    }
  };

  // Render horizontal scroll layout
  const renderHorizontalScroll = () => {
    
    return (
      <div className="w-full overflow-x-auto overflow-y-hidden">
        <div className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent pb-4 min-w-0">
          <div className="flex gap-4">
            {displayProducts.map((product, index) => (
              <div key={index} className={`flex-shrink-0 min-w-0`}>
                <ProductTile
                  product={product}
                  settings={settings}
                  siblings={[]}
                  globals={{}}
                  enableWishlist={true}
                  showTags={true}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render vertical scroll layout with columns
  const renderVerticalScroll = () => {
    let columns;
    if (isMobile) {
      columns = columns_vertical_scroll_mobile;
    } else if (isTablet) {
      columns = columns_vertical_scroll_tablet;
    } else if (isUltrawide) {
      columns = columns_vertical_scroll_ultrawide;
    } else {
      columns = columns_vertical_scroll;
    }
    
    const gridColsClass = `grid-cols-${columns}`;
    
    return (
      <div className="overflow-y-auto max-h-[515px] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
        <div className={`grid ${gridColsClass} gap-x-4 gap-y-6 pr-2`}>
          {displayProducts.map((product, index) => (
            <div key={index} className="w-full">
              <ProductTile
                product={product}
                settings={settings}
                siblings={[]}
                globals={{}}
                enableWishlist={true}
                showTags={true}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render grid layout (static grid without scroll)
  const renderGrid = () => {
    let columns;
    if (isMobile) {
      columns = columns_vertical_scroll_mobile;
    } else if (isTablet) {
      columns = columns_vertical_scroll_tablet;
    } else if (isUltrawide) {
      columns = columns_vertical_scroll_ultrawide;
    } else {
      columns = columns_vertical_scroll;
    }
    
    const gridColsClass = `grid-cols-${columns}`;
    return (
      <div className={`grid ${gridColsClass} gap-4`}>
        {displayProducts.map((product, index) => (
          <div key={index} className="w-full">
            <ProductTile
              product={product}
              settings={settings}
              siblings={[]}
              globals={{}}
              enableWishlist={true}
              showTags={true}
            />
          </div>
        ))}
      </div>
    );
  };

  // Render carousel layout
  const renderCarousel = () => (
    <Splide
      options={splideOptions}
      onMounted={(splide) => {
        splideRef.current = { splide };
      }}
    >
      {displayProducts.map((product, index) => (
        <SplideSlide key={index}>
          <div className='w-full'>
            <ProductTile
              product={product}
              settings={settings}
              siblings={[]}
              globals={{}}
              enableWishlist={true}
              showTags={true}
            />
          </div>
        </SplideSlide>
      ))}
    </Splide>
  );

  // Determine which layout to use based on device
  let currentLayoutType;
  if (isMobile) {
    currentLayoutType = layout_type_mobile;
  } else if (isTablet) {
    currentLayoutType = layout_type_tablet;
  } else if (isUltrawide) {
    currentLayoutType = layout_type_ultrawide;
  } else {
    currentLayoutType = layout_type;
  }
  
  const shouldShowArrows = currentLayoutType === 'carousel' && show_carousel_arrows;

  return (
    <div className={`w-full ${className}`}>
      <div className={`flex flex-row items-center w-full justify-between ${isMobile || isTablet ? 'mb-3' : 'mb-4'}`}>
        <div className={`flex flex-row items-center w-full ${isMobile || isTablet ? 'justify-between' : 'gap-6'}`}>
          <p className={`${isMobile || isTablet ? 'b1--bold' : 'h1'}`}>Products</p>
          <p className={`${isMobile || isTablet ? 'l1--medium' : 'l1--medium'}`}>{totalResults} results found</p>
        </div>
        {shouldShowArrows && (
          <div className='flex items-center space-x-2'>
            <CarouselArrows
              splideRef={splideRef}
              buttonClasses='text-[24px]'
            />
          </div>
        )}
      </div>

      {currentLayoutType === 'horizontal_scroll' && renderHorizontalScroll()}
      {currentLayoutType === 'vertical_scroll' && renderVerticalScroll()}
      {currentLayoutType === 'grid' && renderGrid()}
      {currentLayoutType === 'carousel' && renderCarousel()}

      <div className='flex md:justify-end items-center mt-6'>
        <button
          onClick={onSeeAllProducts}
          className='button button-outline max-md:w-full !px-8'
        >
          View more products
        </button>
      </div>
    </div>
  );
};
